/**
 * 表格列配置 Hook
 */
import { Button, Space, Image } from 'antd';
import RenderEllipsisColumn from '@/components/renderEllipsisColumn';
import { OssEntity } from '@/service/driverArchive/define';
import dayjs from 'dayjs';

interface UseTableColumnsProps {
  onReview: (record: any) => void;
  onViewResult: (record: any) => void;
  onSyncArchive: (record: any) => void;
  onLinkFaceLib: (record: any) => void;
}

export const useTableColumns = ({
  onReview,
  onViewResult,
  onSyncArchive,
  onLinkFaceLib,
}: UseTableColumnsProps) => {


  // 渲染图片 - 支持 OssEntity 数组
  const renderImage = (ossArray: OssEntity[] | null | undefined, maxCount?: number) => {
    if (!ossArray || !Array.isArray(ossArray) || ossArray.length === 0) {
      return '暂无图像';
    }

    const count = ossArray.length;
    const countText = maxCount ? ` (${count}/${maxCount})` : '';

    return (
      <div>
        <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>
          {countText}
        </div>
        <Image.PreviewGroup>
          <Space>
            {ossArray.map((oss, index) => (
              <Image
                key={index}
                src={oss.ossUrl || ''}
                alt="照片"
                width={60}
                height={60} />
            ))}
          </Space>
        </Image.PreviewGroup>
      </div>
    );
  };

  // 渲染是否类型
  const renderYesNo = (value: any) => {
    if (value === 1 || value === true || value === '是') return '是';
    if (value === 0 || value === false || value === '否') return '否';
    return '-';
  };

  const columns: any[] = [
    {
      title: '司机姓名',
      dataIndex: 'name',
      key: 'name',
      width: 100,
      fixed: 'left' as 'left',
      render: (text: any) => <RenderEllipsisColumn text={text} />,
    },
    {
      title: '司机性别',
      dataIndex: 'gender',
      key: 'gender',
      width: 80,
    },
    {
      title: '籍贯',
      dataIndex: 'placeOfOrigin',
      key: 'placeOfOrigin',
      width: 120,
      render: (text: any) => <RenderEllipsisColumn text={text} />,
    },
    {
      title: '出生日期',
      dataIndex: 'birthday',
      key: 'birthday',
      width: 120,
    },
    {
      title: '联系电话',
      dataIndex: 'phone',
      key: 'phone',
      width: 130,
    },
    {
      title: '所属车队',
      dataIndex: 'companyName',
      key: 'companyName',
      width: 150,
      render: (text: any) => <RenderEllipsisColumn text={text} />,
    },
    {
      title: '司机照片',
      dataIndex: 'driverImageFileUrl',
      key: 'driverImageFileUrl',
      width: 300,
      render: (ossArray: OssEntity[]) => renderImage(ossArray), // 司机照片不显示数量限制
    },
    {
      title: '婚姻情况',
      dataIndex: 'maritalStat',
      key: 'maritalStat',
      width: 100,
      render: (text: any) => <RenderEllipsisColumn text={text} />,
    },
    {
      title: '生育情况',
      dataIndex: 'fertilityStat',
      key: 'fertilityStat',
      width: 100,
      render: (text: any) => <RenderEllipsisColumn text={text} />,
    },
    {
      title: '驾龄',
      dataIndex: 'drivingAge',
      key: 'drivingAge',
      width: 80,
      render: (text: any) => (text ? `${text}年` : '-'),
    },
    {
      title: '常居住地',
      dataIndex: 'permResidence',
      key: 'permResidence',
      width: 150,
      render: (text: any) => <RenderEllipsisColumn text={text} />,
    },
    {
      title: '家庭经济情况',
      dataIndex: 'familyEconStat',
      key: 'familyEconStat',
      width: 120,
      render: (text: any) => <RenderEllipsisColumn text={text} />,
    },
    {
      title: '性格',
      dataIndex: 'personality',
      key: 'personality',
      width: 100,
      render: (text: any) => <RenderEllipsisColumn text={text} />,
    },
    {
      title: '是否有辅助驾驶设备使用经验',
      dataIndex: 'assistDrivingExp',
      key: 'assistDrivingExp',
      width: 200,
      render: (text: any) => <RenderEllipsisColumn text={text} />,
    },
    {
      title: '普通话能力',
      dataIndex: 'mandarinAbil',
      key: 'mandarinAbil',
      width: 120,
      render: (text: any) => <RenderEllipsisColumn text={text} />,
    },
    {
      title: '货物运输资质',
      dataIndex: 'cargoTransQual',
      key: 'cargoTransQual',
      width: 150,
      render: (text: any) => <RenderEllipsisColumn text={text} />,
    },
    {
      title: '安全意识',
      dataIndex: 'safetyAware',
      key: 'safetyAware',
      width: 120,
      render: (text: any) => <RenderEllipsisColumn text={text} />,
    },
    {
      title: '驾驶证',
      dataIndex: 'driveLicenseFileUrl',
      key: 'driveLicenseFileUrl',
      width: 300,
      render: (ossArray: OssEntity[]) => renderImage(ossArray, 2), // 驾驶证最多2张
    },
    {
      title: '身份证',
      dataIndex: 'idCardFileUrl',
      key: 'idCardFileUrl',
      width: 300,
      render: (ossArray: OssEntity[]) => renderImage(ossArray, 2), // 身份证最多2张
    },
    {
      title: '信息采集时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 180,
      render: (text: any) => text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      title: '信息采集人员',
      dataIndex: 'createByName',
      key: 'createByName',
      width: 120,
      render: (text: any) => <RenderEllipsisColumn text={text} />,
    },
    {
      title: '是否已关联人脸库',
      dataIndex: 'faceInfoId',
      key: 'faceInfoId',
      width: 140,
      render: (text: any, record: any) => record.reviewStat === 4 ? '是' : '否',
    },
    {
      title: '是否已同步司机档案',
      dataIndex: 'driverId',
      key: 'driverId',
      width: 160,
      render: (text: any, record: any) => record.reviewStat >= 3 ? '是' : '否',
    },
    {
      title: '审核状态',
      dataIndex: 'reviewStatName',
      key: 'reviewStatName',
      width: 120,
      render: (text: any) => <RenderEllipsisColumn text={text} />,
    },
    {
      title: '操作',
      key: 'action',
      width: 350,
      fixed: 'right' as 'right',
      render: (text: any, record: any) => (
        <Space size="small">
          {record.reviewStat === 0 && (
            <Button type="link" size="small" onClick={() => onReview(record)}>
              审核
            </Button>
          )}
          {(record.reviewStat !== 0) && (
            <Button type="link" size="small" onClick={() => onViewResult(record)}>
              查看审核结果
            </Button>
          )}
          {record.reviewStat === 2 && (
            <Button type="link" size="small" onClick={() => onSyncArchive(record)}>
              同步更新司机档案
            </Button>
          )}
          {record.reviewStat === 3 && (
            <Button type="link" size="small" onClick={() => onLinkFaceLib(record)}>
              关联人脸库
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return { columns };
};
